import { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { LogOut, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/api";
import { ArtistsTab } from "@/components/admin/ArtistsTab";
import { PortfolioTab } from "@/components/admin/PortfolioTab";
import { SettingsTab } from "@/components/admin/SettingsTab";

const TABS = [
  ["artists", "Artistas"],
  ["portfolio", "Portfólio"],
  ["settings", "Configurações"],
];

export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("artists");
  const [data, setData] = useState(null);

  const refresh = useCallback(async () => {
    const [a, p, s] = await Promise.all([
      api.get("/artists"), api.get("/portfolio"), api.get("/settings"),
    ]);
    setData({ artists: a.data, portfolio: p.data, settings: s.data });
  }, []);

  useEffect(() => { if (user) refresh().catch(() => {}); }, [user, refresh]);

  if (user === null) return <div className="min-h-screen bg-[#0A0A0B]" data-testid="admin-loading" />;
  if (user === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-[#F4F4F6]" data-testid="admin-panel">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A0A0B]/85 border-b border-white/10 px-4 sm:px-8 h-16 flex items-center justify-between">
        <span className="font-display uppercase text-2xl tracking-widest">Painel Admin</span>
        <div className="flex items-center gap-3">
          <Link to="/" data-testid="admin-view-site"
            className="inline-flex items-center gap-2 font-mono2 text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-white border border-white/15 px-4 py-2 transition-colors">
            <ExternalLink size={13} /> Ver site
          </Link>
          <button onClick={logout} data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 font-mono2 text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-[#E63946] border border-white/15 px-4 py-2 transition-colors">
            <LogOut size={13} /> Sair
          </button>
        </div>
      </header>

      <div className="px-4 sm:px-8 pt-8 flex gap-2 border-b border-white/10" data-testid="admin-tabs">
        {TABS.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
            className={`font-mono2 text-[11px] tracking-[0.2em] uppercase px-5 py-3 border-b-2 -mb-px transition-colors ${tab === id ? "border-[#E63946] text-white" : "border-transparent text-zinc-500 hover:text-white"}`}>
            {label}
          </button>
        ))}
      </div>

      <main className="px-4 sm:px-8 py-10 max-w-6xl">
        {!data ? (
          <p className="font-mono2 text-xs tracking-[0.3em] uppercase text-zinc-500 animate-pulse">Carregando…</p>
        ) : (
          <>
            {tab === "artists" && <ArtistsTab artists={data.artists} refresh={refresh} />}
            {tab === "portfolio" && <PortfolioTab portfolio={data.portfolio} artists={data.artists} refresh={refresh} />}
            {tab === "settings" && <SettingsTab settings={data.settings} refresh={refresh} />}
          </>
        )}
      </main>
    </div>
  );
}
